<script setup lang="ts">
// TODO: render carousel and overlays
defineProps<{
  data: LabsProps
  chapterIndex?: number
  template?: 'page' | 'overlay'
}>()

const $carousel = useTemplateRef<HTMLElement>('$carousel')

const preventOverscroll = debounce(() => {
  if (!$carousel.value) return

  const minBound = 0
  const maxBound = $carousel.value.scrollWidth - window.innerWidth

  if ($carousel.value.scrollLeft < minBound) {
    $carousel.value.scrollLeft = minBound
  } else if ($carousel.value.scrollLeft > maxBound) {
    $carousel.value.scrollLeft = maxBound
  }
}, 500)

function previousSlide() {
  const amount = window.innerWidth >= 1024 ? -0.3 : -0.8

  $carousel.value?.scrollBy({
    left: Math.max($carousel.value?.scrollLeft * -1, window.innerWidth * amount),
    behavior: 'smooth',
  })

  preventOverscroll()
}

function nextSlide() {
  const amount = window.innerWidth >= 1024 ? 0.3 : 0.85

  const currentScroll = $carousel.value?.scrollLeft || 0
  const nextScroll = currentScroll + window.innerWidth * amount
  const maxScroll = ($carousel.value?.scrollWidth || 0) - window.innerWidth
  const distanceToEnd = maxScroll - currentScroll

  $carousel.value?.scrollBy({
    left: nextScroll > maxScroll ? distanceToEnd : window.innerWidth * amount,
    behavior: 'smooth',
  })

  preventOverscroll()
}

const canScrollLeft = ref(false)
const canScrollRight = ref(false)

const { copiedIndex, copyText } = useCopyText()

const updateScrollStatus = () => {
  if (!$carousel.value) return

  canScrollLeft.value = $carousel.value.scrollLeft > 0
  canScrollRight.value =
    $carousel.value.scrollLeft + $carousel.value.clientWidth < $carousel.value.scrollWidth
}

onMounted(() => {
  if (!$carousel.value) return

  setTimeout(() => {
    $carousel.value?.addEventListener('scroll', updateScrollStatus)
    updateScrollStatus()
  })
})

onUnmounted(() => {
  if (!$carousel.value) return
  $carousel.value.removeEventListener('scroll', updateScrollStatus)
})
</script>

<template>
  <section
    class="pt-25 lg:pt-40"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <div class="px-bleed flex flex-col items-center pb-20 text-center">
      <ElementsChapter
        v-if="data.trumpet && chapterIndex"
        :chapter="data.trumpet"
        :index="chapterIndex"
      />
      <ElementsTitleRevealv2
        :title="data.title"
        class="font-display mt-6 max-w-[14em] text-3xl uppercase font-stretch-expanded lg:mt-7 lg:text-5xl"
      />
    </div>

    <div>
      <nav class="absolute z-1 flex w-full justify-between">
        <button
          aria-label="Slide to the previous media"
          class="flex size-10 cursor-pointer items-center justify-center bg-white text-black transition-opacity lg:size-12"
          :class="!canScrollLeft ? 'pointer-events-none opacity-20' : 'opacity-100'"
          @click="previousSlide"
        >
          <IconsArrowE class="h-6 lg:h-8" />
        </button>
        <button
          aria-label="Slide to the next media"
          class="transiton-opacity flex size-10 cursor-pointer items-center justify-center bg-white text-black lg:size-12"
          :class="!canScrollRight ? 'pointer-events-none opacity-20' : 'opacity-100'"
          @click="nextSlide"
        >
          <IconsArrowW class="h-6 lg:h-8" />
        </button>
      </nav>

      <ul ref="$carousel" class="no-scrollbar flex snap-x snap-mandatory overflow-auto">
        <li
          v-for="(item, index) in data.carousel"
          :key="index"
          class="group labs-item relative w-[80vw] shrink-0 snap-center overflow-hidden lg:w-[28vw]"
        >
          <div
           class="content bg-neon-yellow ease-out-quint pointer-events-none absolute right-0 bottom-0 z-[1] flex aspect-[4/5] w-[80%] flex-col p-4 transition-all duration-500 max-lg:w-[66%] xl:w-1/2"
          >
            <p class="font-display text-xl uppercase font-stretch-expanded">{{ item.title }}</p>

            <div>
              <ElementsPortableTextWrapper class="pt-5" :content="item.text" />
              <button
                type="button"
                aria-label="Copy text"
                class="copy-text pointer-events-auto cursor-pointer opacity-60 transition-opacity hover:opacity-100"
                @click.stop="copyText(item.text, index)"
              >
                <IconsCheck v-if="copiedIndex === index" />
                <IconsCopy v-else />
              </button>
            </div>
            

            <!-- just a visual representation of a link -->
            <ElementsLogomarkLink
              v-for="(link, idx) in item.callToAction"
              :key="idx"
              :link="link"
              :dummy="true"
              class="mt-auto"
            />
          </div>

          <!-- the actual link (invisible) -->
          <ElementsLogomarkLink
            v-for="(link, idx) in item.callToAction"
            :key="idx"
            :link="link"
            :make-container-clickable="true"
            :visible="false"
          />

          <CommonMedia
            :sizes="['50vw', '25vw']"
            :aspect="[4 / 5, 3 / 4]"
            :fill="'both'"
            :media="item.media"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="postcss">
.labs-item {
  @media (width >= 64rem) {
    @media (hover: hover) {
      .content {
        transform: translateX(20px);
        opacity: 0;
      }

      &:has(:hover) {
        .content {
          transform: translateX(0px);
          opacity: 1;
        }
      }
    }
  }
}
</style>
